import {Card, CardContent} from '@/components/ui/card'
import {useCalculator} from '@/contexts/CalculatorContext'
import {BanknoteIcon, UserIcon, CalendarIcon} from "lucide-react";

export function MarginBonusPreviousCard() {
    const {
        isKpiMet,
        mdsPerMonth,
        currentPeriod,
        getAllPeriods,
        getNastupyPercentage,
        getMarginPercentage,
        calculate1MarginProfit,
    } = useCalculator()

    const periodData = getAllPeriods()
    const previousPeriod = currentPeriod > 0 ? periodData[currentPeriod - 1] : null
    const previousIsKpiMet = previousPeriod ? previousPeriod.isKpiMet : false
    const previousBonus = previousPeriod ? previousIsKpiMet ? getNastupyPercentage(previousPeriod.nastupy) : 0 : 0

    // Calculate periods to show (only previous periods)
    const periods = [6, 12, 18, 24, 30].filter(months => {
        const periodsBack = Math.floor(months / 6)
        const targetPeriodIndex = currentPeriod - periodsBack
        return targetPeriodIndex >= 0
    })

    // Calculate total profit from all previous periods
    const totalPreviousPeriodsProfits = periods.reduce((total, months) => {
        const periodsBack = Math.floor(months / 6)
        const targetPeriodIndex = currentPeriod - periodsBack
        const historicalPeriod = periodData[targetPeriodIndex]

        if (!historicalPeriod) return total

        return total + calculate1MarginProfit(
            historicalPeriod.margin,
            previousBonus,
            historicalPeriod.nastupy,
            getDiscount(months)
        )
    }, 0)

    function getDiscount(months: number) : number  {
        if ((months === 18 || months === 24) && !isKpiMet) return 0.5;
        if ((months === 30 || months === 36) && !isKpiMet) return 0.25;
        return 1;
    }

    // If no previous periods, don't show the card
    if (periods.length === 0) {
        return null
    }

    return (
        <Card className="transition-all duration-300 border-2 border-green-500 bg-green-50 relative">
            <CardContent className="pt-6">
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <BanknoteIcon className="text-green-500"/>
                        <span className="font-medium">Souhrn za předchozí období</span>
                    </div>
                    <div className="flex justify-between items-center w-full">
                        <span className="text-xs text-muted-foreground">
                            Celkem za konzultanty <br/> z předchozích období:
                        </span>
                        <span className="text-2xl font-bold">
                            {totalPreviousPeriodsProfits.toLocaleString()} Kč
                        </span>
                    </div>
                    {periods.map((months) => {
                        const periodsBack = Math.floor(months / 6)
                        const targetPeriodIndex = currentPeriod - periodsBack
                        // console.log('currentPeriod=' + currentPeriod + ', periodsBack=' + periodsBack + ', targetPeriodIndex=' + targetPeriodIndex + ', months=' + months)
                        const historicalPeriod = periodData[targetPeriodIndex]

                        if (!historicalPeriod) {
                            return null
                        }

                        return (
                            <div key={months} className="w-full pt-2">
                                <div className="flex justify-between items-center w-full">
                                    <span className="text-2xs text-muted-foreground">
                                        <span className="text-xs text-blue-500 inline-flex items-center gap-1">
                                            <CalendarIcon className="w-3 h-3"/> -{months} měsíců
                                        </span><br/>
                                        { ((months === 18 || months === 24) && !isKpiMet) && (
                                            <span className="text-xs text-red-500 inline-flex items-center gap-1">
                                                Redukce kvůli nesplnění KPIs -50%
                                            </span>
                                        )
                                        }
                                        { ((months === 30 || months === 36) && !isKpiMet) && (
                                            <span className="text-xs text-red-500 inline-flex items-center gap-1">
                                                Redukce kvůli nesplnění KPIs -75%
                                            </span>
                                        )
                                        }
                                        <br/>
                                        {historicalPeriod.margin.toLocaleString()} Kč × {historicalPeriod.nastupy}
                                        <UserIcon className="w-3 h-3 inline text-muted-foreground"/> × <br/>
                                        6 měsíců ×({Number(getMarginPercentage(historicalPeriod.margin, getDiscount(months))).toFixed(2)}% + {previousBonus}%) × {mdsPerMonth} MDs
                                    </span>
                                    <span className="text-xl font-bold">
                                        {calculate1MarginProfit(historicalPeriod.margin, previousBonus, historicalPeriod.nastupy, getDiscount(months)).toLocaleString()} Kč
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </CardContent>
        </Card>
    )
} 